// API Base URL - uses Vite proxy in development, direct URL in production
const API_BASE_URL = import.meta.env.PROD
  ? "https://kmfhalal-homepage.onrender.com"
  : "";

// Types
export interface Notice {
  NoticeID: number;
  Title: string;
  Content: string; // Draft.js JSON string
  UpdateDateTime: string;
}

export interface News {
  NewsID: number;
  Title: string;
  Content: string; // Draft.js JSON string
  UpdateDateTime: string;
}

export interface Fatwa {
  FatwaID: number;
  Title: string;
  Content: string; // Draft.js JSON string
  UpdateDateTime: string;
}

export interface AttachedFile {
  AttachedID: number;
  FileRealName: string;
}

// Generic fetch wrapper
async function fetchApi<T>(endpoint: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`);
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

// Notice API
export const noticeApi = {
  list: () => fetchApi<Notice[]>("/api/notice/list"),
  find: (id: number | string) =>
    fetchApi<Notice[]>(`/api/notice/find?id=${id}`),
  files: (id: number | string) =>
    fetchApi<AttachedFile[]>(`/api/notice/files?id=${id}`),
};

// News API (Korean)
export const newsApi = {
  list: () => fetchApi<News[]>("/api/news/kr/list"),
  find: (id: number | string) =>
    fetchApi<News[]>(`/api/news/kr/find?id=${id}`),
};

// Fatwa API
export const fatwaApi = {
  list: () => fetchApi<Fatwa[]>("/api/fatwa/list"),
  find: (id: number | string) =>
    fetchApi<Fatwa[]>(`/api/fatwa/find?id=${id}`),
};

// File download URL helper
export const getFileDownloadUrl = (attachedId: number) =>
  `${API_BASE_URL}/api/attached/download?id=${attachedId}`;

// Draft.js content parser helper
export function parseDraftContent(contentJson: string): string {
  try {
    const content = JSON.parse(contentJson);
    // Simple Draft.js to HTML converter
    // For complex content with images, consider using draft-js-export-html
    if (content.blocks) {
      return content.blocks
        .map((block: { text: string; type: string }) => {
          switch (block.type) {
            case "header-one":
              return `<h1>${block.text}</h1>`;
            case "header-two":
              return `<h2>${block.text}</h2>`;
            case "header-three":
              return `<h3>${block.text}</h3>`;
            case "unordered-list-item":
              return `<li>${block.text}</li>`;
            case "ordered-list-item":
              return `<li>${block.text}</li>`;
            default:
              return `<p>${block.text || "&nbsp;"}</p>`;
          }
        })
        .join("");
    }
    return "";
  } catch {
    return contentJson; // Return as-is if not valid JSON
  }
}
